import { Lock } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LockScreen.css";
import { PassphraseInput } from "./PassphraseInput";

interface Props {
  wrongAttempts: number;
  onUnlock: (passphrase: string) => void;
  onLogout: () => void;
}

export function LockScreen({ wrongAttempts, onUnlock, onLogout }: Props) {
  const { t } = useTranslation();
  const [passphrase, setPassphrase] = useState("");
  const [shaking, setShaking] = useState(false);
  const [shakeNonce, setShakeNonce] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevAttemptsRef = useRef(wrongAttempts);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Open as a modal dialog and auto-focus the input on mount
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    inputRef.current?.focus();
  }, []);

  // Shake animation on wrong attempt (previous prop pattern)
  if (wrongAttempts > prevAttemptsRef.current) {
    prevAttemptsRef.current = wrongAttempts;
    setShaking(true);
    setPassphrase("");
    setShakeNonce((n) => n + 1);
  }

  // Clear the shake flag 400ms after each wrong attempt. The timer id is local,
  // so the cleanup never reads a stale ref; it is cleared on unmount or re-shake.
  useEffect(() => {
    if (shakeNonce === 0) return;
    const id = setTimeout(() => setShaking(false), 400);
    return () => clearTimeout(id);
  }, [shakeNonce]);

  // Capture all keyboard events except those in the passphrase input
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target === inputRef.current) return;
      // Allow Escape to propagate (for double-Esc detection)
      if (e.key === "Escape") return;
      e.stopPropagation();
      e.preventDefault();
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!passphrase) return;
    onUnlock(passphrase);
  }

  return (
    <dialog
      ref={dialogRef}
      className="lock-screen"
      aria-label={t("safety.lock.title")}
      onCancel={(e) => e.preventDefault()}
    >
      <div className="lock-screen__card">
        <picture>
          <source srcSet="/images/hero-unlock-dark.webp" type="image/webp" />
          <img
            className="lock-screen__bg lock-screen__bg--dark"
            src="/images/hero-unlock-dark.jpg"
            alt=""
            aria-hidden="true"
          />
        </picture>
        <picture>
          <source srcSet="/images/hero-unlock-light.webp" type="image/webp" />
          <img
            className="lock-screen__bg lock-screen__bg--light"
            src="/images/hero-unlock-light.jpg"
            alt=""
            aria-hidden="true"
          />
        </picture>
        <div className="lock-screen__content">
          <div className="lock-screen__icon">
            <Lock size={24} aria-hidden="true" />
          </div>
          <h2 className="lock-screen__title">{t("safety.lock.title")}</h2>
          <p className="lock-screen__subtitle">{t("safety.lock.passphrase")}</p>

          <form className="lock-screen__form" onSubmit={handleSubmit}>
            <div className="lock-screen__input-wrapper">
              <PassphraseInput
                ref={inputRef}
                className={`lock-screen__input${shaking ? " lock-screen__input--shake" : ""}`}
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="lock-screen__error" role="alert">
              {wrongAttempts > 0 && t("safety.lock.wrongPassphrase")}
            </div>
            <button type="submit" className="lock-screen__submit" disabled={!passphrase}>
              {t("safety.lock.unlock")}
            </button>
          </form>
          <button type="button" className="lock-screen__logout" onClick={onLogout}>
            {t("auth.switchAccount")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
